import { Container, Box, Grid, GridItem, Heading } from '@chakra-ui/react';
import { useEffect } from 'react';
import { motion, useAnimation, AnimationControls } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { StaticImageData } from 'next/image';

const MotionBox = motion(Box);

const Icon = ({
  url,
  position,
  animate,
  initial,
  custom,
}: {
  url: StaticImageData;
  position: {
    top: string;
    left: string;
  };
  animate: AnimationControls;
  initial: object;
  custom: number;
}) => {
  return (
    <MotionBox
      bg={`url(${url.src}) center/contain no-repeat`}
      position="absolute"
      top={position.top}
      left={position.left}
      w={{
        base: '4rem',
        md: '6rem',
      }}
      h={{
        base: '4rem',
        md: '6rem',
      }}
      animate={animate}
      initial={initial}
      custom={custom}
      viewport={{ once: true }}
    />
  );
};

interface WorldProps {
  world: {
    title: string;
    text: string;
    country: string;
    stats?: {
      value: string;
      text: string;
      bgColor: string;
    }[];
  };
  map: any;
  icons: {
    url: StaticImageData;
    position: {
      top: string;
      left: string;
    };
  }[];
}

export default function World({ world, map, icons }: WorldProps) {
  const iconAnimation = useAnimation();
  const [ref, inView] = useInView({
    rootMargin: '-40% 0%',
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      iconAnimation.start((i) => ({
        y: 0,
        opacity: 1,
        transition: {
          delay: i * 0.5,
          duration: 1.5,
          type: 'spring',
          bounce: 0.5,
        },
      }));
    }
  }, [iconAnimation, inView]);

  const numbers = Array.from(Array(icons.length).keys());

  const shuffled = numbers
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);

  return (
    <Box my={{ base: '12', lg: '20' }}>
      <Container>
        <Heading
          as="h2"
          textAlign={{ lg: 'center' }}
          fontSize={{ base: '2.5rem', lg: '3rem' }}
          lineHeight={{ base: '3rem', lg: '3.5rem' }}
          mb={{ base: '2', lg: '4' }}
        >
          {world.title}
        </Heading>
        <Box
          fontSize={{ base: '1.125rem', lg: '1.25rem' }}
          mb={{ base: '6', lg: '8' }}
          textAlign={{ lg: 'center' }}
        >
          {world.text}
        </Box>
        <Box display={{ base: 'block', lg: 'block' }} mb="8" ref={ref}>
          <Box
            bg={`url(${map.src}) center/contain no-repeat`}
            w="100%"
            h={{
              base: '300px',
              md: '450px',
              lg: '450px',
              xl: '500px',
              '2xl': '600px',
            }}
            position="relative"
          >
            {icons.map((icon, index) => {
              return (
                <Icon
                  key={index}
                  url={icon.url}
                  position={icon.position}
                  animate={iconAnimation}
                  initial={{ y: -30, opacity: 0 }}
                  custom={shuffled[index]}
                />
              );
            })}
          </Box>
        </Box>
        <Heading as="h3" textAlign="center" mb={{ base: '4', lg: '8' }}>
          {world.country}
        </Heading>
        {world.stats && (
          <Grid gridTemplateColumns={{ lg: '1fr 1fr 1fr' }} gap={{ base: '6', lg: '5' }}>
            {world.stats.map((stat) => (
              <GridItem
                key={stat.value}
                bgColor={stat.bgColor}
                py={{ base: '6', lg: '8' }}
                px="8"
                borderRadius="1rem"
                textAlign="center"
              >
                <Box
                  fontSize={{ base: '3.5rem', lg: '4.5rem' }}
                  lineHeight={{ base: '4rem', lg: '4.5rem' }}
                  fontWeight="bold"
                >
                  {stat.value}
                </Box>
                <Box fontSize={{ base: '1rem' }} lineHeight={{ base: '1.5rem' }}>
                  {stat.text}
                </Box>
              </GridItem>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
